/**
 * Promo Gift Popup — Control Fresh v3
 * Compatible con tema Magic (Shopify OS 2.0)
 *
 * LÓGICA DEL DISPARADOR:
 * - Suma las unidades de TODOS los productos activadores en el carrito
 * - Si esa suma >= cantidad mínima configurada → muestra el popup
 * - Funciona con cualquier combinación: 1+1, 2+0, 0+2, etc.
 *
 * Ejemplo con min=2:
 *   1 Body verde oliva + 1 Top palo de rosa = 2 ✅ activa
 *   2 unidades del mismo Body               = 2 ✅ activa
 *   Solo 1 prenda elegible                  = 1 ❌ no activa
 *
 * Detección por 3 métodos:
 *   1. theme.pubsub (cart drawer del tema Magic)
 *   2. Intercept fetch + XHR (/cart/add)
 *   3. Polling /cart.js cada 2.5s como fallback
 */

class PromoGiftPopup extends HTMLElement {
  constructor() {
    super();
    this._hasShownThisSession = false;
    this._unsubscribePubsub   = null;
    this._lastCartToken       = null;
    this._pollInterval        = null;
  }

  connectedCallback() {
    // Cerrar con backdrop o botón ×
    this.querySelector('[data-backdrop]')
      ?.addEventListener('click', () => this.close());
    this.querySelectorAll('[data-close]')
      .forEach(btn => btn.addEventListener('click', () => this.close()));

    // Tecla ESC
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.classList.contains('is-visible')) this.close();
    });

    // Activar los 3 métodos de detección
    this._subscribePubsub();
    this._interceptFetch();
    this._startPolling();
  }

  disconnectedCallback() {
    if (this._unsubscribePubsub) this._unsubscribePubsub();
    if (this._pollInterval) clearInterval(this._pollInterval);
  }

  // ─── MÉTODO 1: pubsub de Magic ────────────────────────────────────────────
  _subscribePubsub(attempts = 0) {
    if (window.theme?.pubsub?.PUB_SUB_EVENTS) {
      this._unsubscribePubsub = window.theme.pubsub.subscribe(
        window.theme.pubsub.PUB_SUB_EVENTS.cartUpdate,
        (event) => {
          if (event?.cart) this._checkConditions(event.cart);
        }
      );
    } else if (attempts < 40) {
      setTimeout(() => this._subscribePubsub(attempts + 1), 250);
    }
  }

  // ─── MÉTODO 2: interceptar fetch y XHR ───────────────────────────────────
  _interceptFetch() {
    const self = this;

    // fetch API
    const originalFetch = window.fetch;
    window.fetch = function(...args) {
      const url = typeof args[0] === 'string' ? args[0] : (args[0]?.url || '');
      const isCartAdd = url.includes('/cart/add');
      return originalFetch.apply(this, args).then(response => {
        if (isCartAdd) setTimeout(() => self._fetchCartAndCheck(), 800);
        return response;
      });
    };

    // XMLHttpRequest (apps de terceros)
    const originalOpen = XMLHttpRequest.prototype.open;
    const originalSend = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.open = function(method, url, ...rest) {
      this._xhrUrl = url;
      return originalOpen.apply(this, [method, url, ...rest]);
    };
    XMLHttpRequest.prototype.send = function(...args) {
      if (this._xhrUrl?.includes('/cart/add')) {
        this.addEventListener('load', () => setTimeout(() => self._fetchCartAndCheck(), 800));
      }
      return originalSend.apply(this, args);
    };
  }

  // ─── MÉTODO 3: polling ────────────────────────────────────────────────────
  _startPolling() {
    this._pollInterval = setInterval(() => {
      if (this._hasShownThisSession) {
        clearInterval(this._pollInterval);
        return;
      }
      this._fetchCartAndCheck();
    }, 2500);
  }

  _fetchCartAndCheck() {
    fetch('/cart.js')
      .then(r => r.json())
      .then(cart => {
        // Evitar procesar el mismo estado dos veces
        const token = cart.token + '_' + cart.item_count;
        if (token === this._lastCartToken) return;
        this._lastCartToken = token;
        this._checkConditions(cart);
      })
      .catch(() => {});
  }

  // ─── LÓGICA PRINCIPAL ─────────────────────────────────────────────────────
  _checkConditions(cart) {
    if (!cart?.items) return;

    // Guards de frecuencia
    if (this._hasShownThisSession) return;
    if (this.dataset.showOnce === 'true' &&
        localStorage.getItem('promo_gift_popup_shown') === 'true') return;

    const triggerIds  = this._getTriggerIds();
    const minQuantity = parseInt(this.dataset.minQuantity || '2', 10);

    let totalEligibleUnits = 0;

    if (triggerIds.length === 0) {
      // Sin productos configurados → contar todo el carrito
      totalEligibleUnits = cart.item_count;
    } else {
      /*
       * Sumar las unidades de TODOS los items cuyo product_id
       * esté en la lista de activadores, sin importar el orden.
       *
       * product_id en cart.js siempre es número entero.
       * trigger_ids vienen del Liquid como strings numéricos.
       */
      totalEligibleUnits = cart.items.reduce((sum, item) => {
        const pid = String(item.product_id);
        const isEligible = triggerIds.includes(pid);
        return isEligible ? sum + item.quantity : sum;
      }, 0);
    }

    if (totalEligibleUnits >= minQuantity) {
      setTimeout(() => this.open(), 500);
    }
  }

  _getTriggerIds() {
    const raw = this.dataset.triggerProductIds || '';
    // Limpiar espacios y filtrar vacíos, mantener como strings para comparar con product_id
    return raw.split(',').map(s => s.trim()).filter(Boolean);
  }

  // ─── ABRIR / CERRAR ───────────────────────────────────────────────────────
  open() {
    if (this._hasShownThisSession) return;
    this._hasShownThisSession = true;

    if (this.dataset.showOnce === 'true') {
      localStorage.setItem('promo_gift_popup_shown', 'true');
    }

    this.classList.add('is-visible');
    document.body.style.overflow = 'hidden';

    // Focus al primer elemento interactivo
    const focusable = this.querySelector('a, button, [tabindex]');
    if (focusable) focusable.focus();
  }

  close() {
    this.classList.remove('is-visible');
    document.body.style.overflow = '';
  }
}

customElements.define('promo-gift-popup', PromoGiftPopup);